/**
 * Work Compliance API Service
 * TypeScript client for work documentation and compliance endpoints
 * Trust Marketplace - Work Documentation System
 */

import axios, { AxiosInstance } from 'axios';
import { getWeaveinApiUrl } from '@/src/config';

// ============================================================================
// INTERFACES
// ============================================================================

export interface WorkActivitySubmission {
  enrollment_id: number;
  month: number;
  year: number;
  employment_hours: number;
  education_hours: number;
  volunteer_hours: number;
  job_training_hours: number;
  job_search_hours: number;
  community_service_hours: number;
  employer_name?: string;
  education_institution?: string;
  volunteer_organization?: string;
  training_program_name?: string;
  notes?: string;
}

export interface WorkSubmissionResponse {
  id: number;
  enrollment_id: number;
  month: number;
  year: number;
  total_hours: number;
  meets_requirement: boolean;
  verification_status: string;
  message: string;
  next_steps: string[];
}

export interface ComplianceDashboard {
  enrollment_id: number;
  member_id: number;
  current_month: number;
  current_year: number;
  current_month_hours: number;
  current_month_compliant: boolean;
  current_month_status: string;
  deadline_date: string;
  days_until_deadline: number;
  hours_needed: number;
  last_6_months_compliance: MonthlyCompliance[];
  consecutive_compliant_months: number;
  active_alerts: Alert[];
  pending_verifications: number;
  total_points_earned: number;
  total_incentives_earned: number;
  total_incentives_paid: number;
  incentives_pending: number;
}

export interface MonthlyCompliance {
  month: number;
  year: number;
  total_hours: number;
  compliant: boolean;
  verification_status: string;
  submitted_date?: string;
}

export interface Alert {
  id: number;
  type: string;
  severity: string;
  title: string;
  message: string;
  deadline_date?: string;
}

export interface VerificationQueueItem {
  id: number;
  enrollment_id: number;
  member_id: number;
  member_name: string;
  month: number;
  year: number;
  total_hours: number;
  employment_hours: number;
  education_hours: number;
  volunteer_hours: number;
  job_training_hours: number;
  meets_requirement: boolean;
  verification_status: string;
  submitted_date: string | null;
  has_documents: boolean;
  document_count: number;
  employer_name: string | null;
  submission_method: string;
}

export interface VerificationAction {
  work_activity_log_id: number;
  verification_result: 'APPROVED' | 'REJECTED' | 'PARTIALLY_APPROVED' | 'PENDING_INFO';
  hours_approved?: number;
  hours_rejected?: number;
  rejection_reason?: string;
  verification_notes?: string;
  verification_method?: string;
  time_spent_minutes?: number;
}

export interface VerificationResponse {
  success: boolean;
  work_activity_id: number;
  verification_result: string;
  is_compliant: boolean;
  milestones_triggered: MilestoneTrigger[];
  total_member_payout: number;
  total_cbo_payout: number;
  message: string;
}

export interface MilestoneTrigger {
  milestone_id: number;
  milestone_name: string;
  milestone_description: string;
  member_payout: number;
  cbo_payout: number;
  points: number;
  progress_status: string;
}

export interface CBODashboard {
  cbo_id: number;
  program_id: number | null;
  total_members: number;
  compliant_count: number;
  compliance_rate: number;
  pending_verifications: number;
  at_risk_count: number;
  at_risk_members: AtRiskMember[];
  current_month: number;
  current_year: number;
}

export interface AtRiskMember {
  enrollment_id: number;
  member_id: number;
  member_name: string;
  hours_submitted: number;
  hours_needed: number;
  days_remaining?: number;
  status?: string;
}

export interface DocumentUploadMetadata {
  work_activity_log_id: number;
  enrollment_id: number;
  document_type: string;
  is_primary: boolean;
}

export interface UploadedDocument {
  id: number;
  file_name: string;
  document_type: string;
  file_size_bytes: number;
  uploaded_at: string;
  verification_status: string;
  is_primary: boolean;
}

export interface ComplianceHistory {
  submissions: WorkSubmissionDetail[];
  total_count: number;
}

export interface WorkSubmissionDetail {
  id: number;
  month: number;
  year: number;
  total_hours: number;
  employment_hours: number;
  education_hours: number;
  volunteer_hours: number;
  job_training_hours: number;
  job_search_hours: number;
  community_service_hours: number;
  employer_name: string | null;
  education_institution: string | null;
  volunteer_organization: string | null;
  meets_requirement: boolean;
  verification_status: string;
  submitted_date: string | null;
  verified_date: string | null;
  verified_by_name: string | null;
  verification_notes: string | null;
  rejection_reason: string | null;
  document_count: number;
  milestones_triggered: any[];
  incentive_earned: boolean;
  incentive_amount: number;
}

// ============================================================================
// API CLIENT CLASS
// ============================================================================

class WorkComplianceAPI {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: getWeaveinApiUrl('/api/work-compliance'),
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add auth token interceptor
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  // ============================================================================
  // MEMBER ENDPOINTS
  // ============================================================================

  /**
   * Submit monthly work activity hours
   */
  async submitWorkActivity(data: WorkActivitySubmission): Promise<WorkSubmissionResponse> {
    const response = await this.client.post<WorkSubmissionResponse>('/submit-work-activity', data);
    return response.data;
  }

  /**
   * Get member compliance dashboard
   */
  async getMemberDashboard(memberId: number): Promise<ComplianceDashboard> {
    const response = await this.client.get<ComplianceDashboard>(`/member/${memberId}/dashboard`);
    return response.data;
  }

  /**
   * Get member compliance history
   */
  async getMemberHistory(enrollmentId: number): Promise<ComplianceHistory> {
    const response = await this.client.get<ComplianceHistory>(`/history?enrollment_id=${enrollmentId}`);
    return response.data;
  }

  /**
   * Upload work documentation
   */
  async uploadDocument(
    file: File,
    metadata: DocumentUploadMetadata
  ): Promise<{ success: boolean; document_id: number }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('work_activity_log_id', metadata.work_activity_log_id.toString());
    formData.append('enrollment_id', metadata.enrollment_id.toString());
    formData.append('document_type', metadata.document_type);
    formData.append('is_primary', metadata.is_primary.toString());

    const response = await this.client.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  /**
   * Get uploaded documents for a work activity
   */
  async getDocuments(workActivityLogId: number): Promise<{ documents: UploadedDocument[] }> {
    const response = await this.client.get(`/documents?work_activity_log_id=${workActivityLogId}`);
    return response.data;
  }

  /**
   * Delete a document
   */
  async deleteDocument(documentId: number): Promise<{ success: boolean }> {
    const response = await this.client.delete(`/documents/${documentId}`);
    return response.data;
  }

  // ============================================================================
  // CBO ENDPOINTS
  // ============================================================================

  /**
   * Get CBO work verification queue
   */
  async getVerificationQueue(
    cboId: number,
    filters?: {
      verification_status?: string;
      month?: number;
      year?: number;
      limit?: number;
      offset?: number;
    }
  ): Promise<{ total_count: number; results: VerificationQueueItem[] }> {
    const params = new URLSearchParams({
      cbo_id: cboId.toString(),
      ...filters
    } as any);

    const response = await this.client.get(`/cbo/verification-queue?${params}`);
    return response.data;
  }

  /**
   * Verify a work activity submission
   */
  async verifySubmission(action: VerificationAction): Promise<VerificationResponse> {
    const response = await this.client.post<VerificationResponse>('/cbo/verify', action);
    return response.data;
  }

  /**
   * Get CBO compliance dashboard
   */
  async getCBODashboard(cboId: number, programId?: number): Promise<CBODashboard> {
    const params = new URLSearchParams({ cbo_id: cboId.toString() });
    if (programId) {
      params.append('program_id', programId.toString());
    }

    const response = await this.client.get<CBODashboard>(`/cbo/dashboard?${params}`);
    return response.data;
  }

  /**
   * Get compliance trends
   */
  async getComplianceTrends(
    cboId: number,
    months: number = 6
  ): Promise<{ trends: Array<{ month: number; year: number; compliance_rate: number }> }> {
    const response = await this.client.get(`/cbo/trends?cbo_id=${cboId}&months=${months}`);
    return response.data;
  }

  // ============================================================================
  // ALERTS
  // ============================================================================

  /**
   * Get active compliance alerts
   */
  async getAlerts(enrollmentId: number): Promise<{ alerts: Alert[] }> {
    const response = await this.client.get(`/alerts?enrollment_id=${enrollmentId}`);
    return response.data;
  }

  /**
   * Acknowledge an alert
   */
  async acknowledgeAlert(alertId: number): Promise<{ success: boolean }> {
    const response = await this.client.post(`/alerts/${alertId}/acknowledge`);
    return response.data;
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Calculate total hours from activity breakdown
   */
  calculateTotalHours(hours: Partial<WorkActivitySubmission>): number {
    return (
      (hours.employment_hours || 0) +
      (hours.education_hours || 0) +
      (hours.volunteer_hours || 0) +
      (hours.job_training_hours || 0) +
      (hours.job_search_hours || 0) +
      (hours.community_service_hours || 0)
    );
  }

  /**
   * Check if hours meet requirement (≥80)
   */
  meetsRequirement(hours: Partial<WorkActivitySubmission>): boolean {
    return this.calculateTotalHours(hours) >= 80;
  }

  /**
   * Get month name from number
   */
  getMonthName(month: number): string {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month - 1] || '';
  }

  /**
   * Format file size
   */
  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const workComplianceApi = new WorkComplianceAPI();

export default workComplianceApi;
